import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useCourseContent } from '../../contexts/CourseContentContext';
import { useEvaluation } from '../../contexts/EvaluationContext';
import {
  Play,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Video,
  Code,
  BookOpen,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Download,
  Share2,
  HelpCircle,
  ClipboardList,
  Award,
  AlertTriangle,
  Target,
} from 'lucide-react';

const SAFE_HTML_CONFIG = {
  ALLOWED_TAGS: ['p','br','strong','em','ul','ol','li','h1','h2','h3','h4',
                 'blockquote','code','pre','a','img','table','thead','tbody',
                 'tr','th','td','span','div'],
  ALLOWED_ATTR: ['href','src','alt','class','target','rel'],
  ALLOW_DATA_ATTR: false,
};

const CourseViewer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const {
    getCourseContent,
    getCourseProgress,
    markLessonCompleted,
    loadCourseContents,
    loadCourseProgress,
    getCourseProgressServer,
  } = useCourseContent();
  const { loadCourseEvaluations, getEvaluationsByCourse } = useEvaluation();

  const [expandedModules, setExpandedModules] = useState({});
  const [currentLesson, setCurrentLesson] = useState(null);
  const [quizAnswers, setQuizAnswers]     = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [watchedVideoIds, setWatchedVideoIds] = useState(new Set());
  const [sidebarHeight, setSidebarHeight] = useState(null);
  // Ref pour éviter les appels API multiples avant que le state ne se mette à jour
  const watchedVideoIdsRef = useRef(new Set());
  const sidebarContentRef  = useRef(null);
  const isDragging         = useRef(false);
  const dragStartY         = useRef(0);
  const dragStartHeight    = useRef(0);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = e.clientY - dragStartY.current;
      setSidebarHeight(Math.max(150, dragStartHeight.current + delta));
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // Charge le contenu, les évaluations, la progression
  useEffect(() => {
    if (!courseId) return;
    loadCourseContents(courseId);
    loadCourseEvaluations(courseId);
    loadCourseProgress(courseId);
  }, [courseId, loadCourseContents, loadCourseEvaluations, loadCourseProgress]);

  // Réinitialise le quiz quand on change de leçon
  useEffect(() => {
    setQuizAnswers({});
    setQuizSubmitted(false);
  }, [currentLesson?.id]);

  const courseContent = getCourseContent(courseId);
  const progress      = getCourseProgress(courseId);
  const evaluations   = getEvaluationsByCourse(courseId);
  const serverProgress = getCourseProgressServer(courseId);

  // Évaluation indexée par module_id
  const evalByModule = {};
  (evaluations || []).forEach(ev => { evalByModule[ev.module_id] = ev; });

  // Liste plate de toutes les leçons dans l'ordre
  const allLessons = useMemo(() =>
    (courseContent?.modules || []).flatMap(m =>
      (m.lessons || []).map(l => ({ ...l, moduleId: m.id }))
    ),
  [courseContent]);

  // Initialise les vidéos déjà visionnées depuis la progression serveur
  // (placé après allLessons et serverProgress pour éviter le no-use-before-define)
  useEffect(() => {
    if (!serverProgress || !allLessons.length) return;
    const unwatchedIds = new Set((serverProgress.unwatched_video_lessons || []).map(l => l.id));
    const watchedIds = new Set(
      allLessons
        .filter(l => l.type === 'video' && !unwatchedIds.has(l.id))
        .map(l => l.id)
    );
    watchedVideoIdsRef.current = watchedIds;
    setWatchedVideoIds(watchedIds);
  }, [serverProgress, allLessons]);

  // Tout est validé mais pas de certificat (évaluations réussies avant la fin des vidéos, ou
  // certificat à régler) : on demande au serveur de l'émettre et on affiche sa réponse.
  const [reclamation, setReclamation] = useState({ enCours: false, raison: null, lienAchat: null });
  const reclamerCertificat = useCallback(async () => {
    setReclamation({ enCours: true, raison: null, lienAchat: null });
    try {
      const { data } = await axios.post(`/api/courses/${courseId}/issue-certificate`);
      if (data?.eligible) { setReclamation({ enCours: false, raison: null, lienAchat: null }); loadCourseProgress(courseId); return; }
      const lien = data?.paiementRequis && typeof data?.lienAchat === 'string' && data.lienAchat.startsWith('https://app.innovactive360.cloud/') ? data.lienAchat : null;
      setReclamation({ enCours: false, raison: data?.reason || 'Le certificat ne peut pas encore être délivré.', lienAchat: lien });
    } catch {
      setReclamation({ enCours: false, raison: 'Vérification impossible pour le moment. Réessayez.', lienAchat: null });
    }
  }, [courseId, loadCourseProgress]);

  const refreshProgress = useCallback(() => {
    loadCourseProgress(courseId);
    loadCourseEvaluations(courseId);
  }, [courseId, loadCourseProgress, loadCourseEvaluations]);

  // Marque une vidéo comme visionnée (appel API unique grâce à la ref)
  const markVideoWatched = useCallback(async (lessonId) => {
    if (watchedVideoIdsRef.current.has(lessonId)) return;
    watchedVideoIdsRef.current.add(lessonId);
    try {
      await axios.post(`/api/lessons/${lessonId}/video-watched`);
      setWatchedVideoIds(prev => new Set([...prev, lessonId]));
    } catch (err) {
      watchedVideoIdsRef.current.delete(lessonId);
      console.error('[CourseViewer] Erreur marquage vidéo:', err.message);
    }
  }, []);

  // Appelé à chaque tick de la vidéo — déclenche markVideoWatched à 80 %
  const handleVideoTimeUpdate = useCallback((e, lessonId) => {
    const video = e.target;
    if (!video.duration || watchedVideoIdsRef.current.has(lessonId)) return;
    if (video.currentTime / video.duration >= 0.80) {
      markVideoWatched(lessonId);
    }
  }, [markVideoWatched]);

  const currentLessonIndex = useMemo(() =>
    allLessons.findIndex(l => l.id === currentLesson?.id),
  [allLessons, currentLesson]);

  const goToLesson = useCallback((index) => {
    if (index < 0 || index >= allLessons.length) return;
    const lesson = allLessons[index];
    setCurrentLesson({ ...lesson, moduleId: lesson.moduleId });
    markLessonCompleted(courseId, lesson.moduleId, lesson.id);
    setExpandedModules(prev => ({ ...prev, [lesson.moduleId]: true }));
  }, [allLessons, courseId, markLessonCompleted]);

  // Sélection automatique de la première leçon au chargement
  useEffect(() => {
    if (!currentLesson && allLessons.length > 0) {
      const first = allLessons[0];
      setCurrentLesson(first);
      setExpandedModules({ [first.moduleId]: true });
    }
  }, [allLessons, currentLesson]);

  if (!courseContent) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement du cours…</p>
        </div>
      </div>
    );
  }

  if (!courseContent.modules?.length) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Aucun contenu disponible
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Ce cours ne contient pas encore de leçons publiées.
        </p>
      </div>
    );
  }

  const toggleModule = (moduleId) =>
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));

  const handleLessonClick = (lesson, moduleId) => {
    const idx = allLessons.findIndex(l => l.id === lesson.id);
    if (idx !== -1) goToLesson(idx);
    else {
      setCurrentLesson({ ...lesson, moduleId });
      markLessonCompleted(courseId, moduleId, lesson.id);
    }
  };

  const handleStartEvaluation = (evaluationId) => {
    navigate(`/evaluation/${evaluationId}/take?courseId=${courseId}`);
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video':       return <Video       className="w-4 h-4" />;
      case 'interactive': return <Play        className="w-4 h-4" />;
      case 'exercise':    return <Code        className="w-4 h-4" />;
      case 'document':    return <FileText    className="w-4 h-4" />;
      case 'quiz':        return <HelpCircle  className="w-4 h-4" />;
      case 'assignment':  return <ClipboardList className="w-4 h-4" />;
      default:            return <BookOpen    className="w-4 h-4" />;
    }
  };

  const getLessonTypeColor = (type) => {
    switch (type) {
      case 'video':       return 'text-red-600 dark:text-red-400';
      case 'interactive': return 'text-blue-600 dark:text-blue-400';
      case 'exercise':    return 'text-green-600 dark:text-green-400';
      case 'document':    return 'text-purple-600 dark:text-purple-400';
      case 'quiz':        return 'text-yellow-600 dark:text-yellow-400';
      case 'assignment':  return 'text-orange-600 dark:text-orange-400';
      default:            return 'text-gray-600 dark:text-gray-400';
    }
  };

  // ── Rendu du contenu d'une leçon ─────────────────────────────────────────

  const renderLessonContent = (lesson) => {
    const { type, content = {} } = lesson;

    if (type === 'video') {
      const embedUrl = getEmbedUrl(content.videoUrl);
      const isWatched = watchedVideoIds.has(lesson.id);
      return (
        <div className="space-y-4">
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {embedUrl ? (
              // YouTube / Vimeo
              <iframe
                src={embedUrl}
                className="w-full h-full"
                allowFullScreen
                title={lesson.title}
              />
            ) : content.videoUrl ? (
              // Cloudinary ou URL directe MP4
              <video
                key={lesson.id}
                src={content.videoUrl}
                controls
                controlsList="nodownload"
                className="w-full h-full"
                onTimeUpdate={(e) => handleVideoTimeUpdate(e, lesson.id)}
                onEnded={() => markVideoWatched(lesson.id)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Play className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">URL vidéo non disponible</p>
                </div>
              </div>
            )}
          </div>

          {/* Bandeau d'avancement vidéo */}
          {content.videoUrl && (
            <div className={`flex items-center gap-2 text-sm rounded-lg p-3 border ${
              isWatched
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400'
            }`}>
              {isWatched ? (
                <><CheckCircle className="w-4 h-4 flex-shrink-0" /> Vidéo visionnée — leçon suivante débloquée.</>
              ) : (
                <><AlertTriangle className="w-4 h-4 flex-shrink-0" /> Regardez au moins 80 % de la vidéo pour débloquer la leçon suivante.</>
              )}
            </div>
          )}

          {content.transcript && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Transcription</h3>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{content.transcript}</p>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (type === 'document') {
      const docUrl = content.documentUrl;
      const isOneDrive = docUrl && (
        docUrl.includes('1drv.ms') ||
        docUrl.includes('onedrive.live.com') ||
        docUrl.includes('sharepoint.com')
      );
      const isGDrive = docUrl && (
        docUrl.includes('drive.google.com') ||
        docUrl.includes('docs.google.com')
      );
      const isPdf = docUrl && !isOneDrive && !isGDrive && (
        docUrl.includes('.pdf') || docUrl.includes('cloudinary')
      );

      let embedUrl = null;
      if (isPdf) {
        embedUrl = docUrl;
      } else if (isOneDrive) {
        embedUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(docUrl)}`;
      } else if (isGDrive) {
        const fileId = docUrl.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1];
        if (fileId) embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
      }

      return (
        <div className="space-y-4">
          {content.text && (
            <div
              className="prose dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.text, SAFE_HTML_CONFIG) }}
            />
          )}

          {docUrl && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-gray-900 dark:text-white">Document</h3>
                <a
                  href={docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
                >
                  <Download className="w-4 h-4" />
                  {content.documentName || 'Ouvrir'}
                </a>
              </div>
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  className="w-full border border-gray-200 dark:border-gray-700 rounded-lg"
                  style={{ height: '500px' }}
                  title={content.documentName || 'Document'}
                  allow="autoplay"
                />
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <FileText className="w-8 h-8 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {content.documentName || 'Document'}
                    </p>
                    <p className="text-xs text-gray-500">Cliquez sur "Ouvrir" pour accéder au document</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {content.attachments?.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Fichiers joints</h3>
              <div className="space-y-2">
                {content.attachments.map((att, i) => {
                  const attUrl = att.url || att;
                  const attName = att.name || (typeof att === 'string' ? att.split('/').pop() : 'Fichier');
                  return (
                    <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <span className="text-sm text-gray-900 dark:text-white flex-1 truncate">{attName}</span>
                      <a
                        href={attUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto text-primary hover:text-primary/80 flex-shrink-0"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!content.text && !docUrl && !content.attachments?.length && (
            <p className="text-gray-500 dark:text-gray-400 italic">Aucun contenu disponible.</p>
          )}
        </div>
      );
    }

    if (type === 'quiz') {
      const questions = content.questions || [];
      const answeredCount = Object.keys(quizAnswers).length;
      const quizScore = quizSubmitted
        ? questions.reduce((acc, q, qi) => acc + (quizAnswers[qi] === q.correct ? 1 : 0), 0)
        : null;

      return (
        <div className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-200">
            Auto-évaluation pédagogique · {questions.length} question{questions.length > 1 ? 's' : ''}
            {content.timeLimit ? ` · ${content.timeLimit} min` : ''}
          </div>

          {quizSubmitted && (
            <div className={`rounded-lg p-4 text-center font-semibold ${
              quizScore === questions.length
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                : quizScore >= Math.ceil(questions.length * 0.6)
                ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
            }`}>
              Score : {quizScore} / {questions.length}
            </div>
          )}

          {questions.map((q, qi) => {
            const selected = quizAnswers[qi];
            const isCorrect = quizSubmitted && selected === q.correct;
            const isWrong   = quizSubmitted && selected !== undefined && selected !== q.correct;
            return (
              <div key={qi} className={`border rounded-lg p-4 ${
                isCorrect ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
                : isWrong ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
                : 'border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex items-start gap-2 mb-3">
                  {isCorrect && <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />}
                  {isWrong   && <XCircle    className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />}
                  <p className="font-medium text-gray-900 dark:text-white">
                    {qi + 1}. {q.question}
                  </p>
                </div>
                <div className="space-y-2">
                  {(q.options || []).map((opt, oi) => {
                    const isSelected  = selected === oi;
                    const isRightOpt  = quizSubmitted && oi === q.correct;
                    const isWrongOpt  = quizSubmitted && isSelected && oi !== q.correct;
                    return (
                      <label
                        key={oi}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer border transition-colors ${
                          isRightOpt ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                          : isWrongOpt ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
                          : isSelected ? 'border-primary bg-primary/5'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`quiz-${lesson.id}-q${qi}`}
                          checked={isSelected}
                          onChange={() => { if (!quizSubmitted) setQuizAnswers(prev => ({ ...prev, [qi]: oi })); }}
                          disabled={quizSubmitted}
                          className="text-primary"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{opt}</span>
                        {isRightOpt && <CheckCircle className="w-4 h-4 text-green-600 ml-auto flex-shrink-0" />}
                      </label>
                    );
                  })}
                </div>
                {quizSubmitted && q.explanation && (
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded p-2 italic">
                    {q.explanation}
                  </p>
                )}
              </div>
            );
          })}

          {!quizSubmitted ? (
            <button
              onClick={() => setQuizSubmitted(true)}
              disabled={answeredCount < questions.length}
              className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {answeredCount < questions.length
                ? `Répondre à toutes les questions (${answeredCount}/${questions.length})`
                : 'Vérifier mes réponses'}
            </button>
          ) : (
            <button
              onClick={() => { setQuizAnswers({}); setQuizSubmitted(false); }}
              className="w-full py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
            >
              Recommencer le quiz
            </button>
          )}
        </div>
      );
    }

    if (type === 'exercise') {
      return (
        <div className="space-y-4">
          {content.instructions && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Instructions</h3>
              <p className="text-gray-600 dark:text-gray-400">{content.instructions}</p>
            </div>
          )}
          {content.starterCode && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Code de départ</h3>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{content.starterCode}</code>
              </pre>
            </div>
          )}
          {content.testCases?.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Tests</h3>
              <div className="space-y-2">
                {content.testCases.map((test, i) => (
                  <div key={i} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-sm text-gray-600 dark:text-gray-400">
                    {test.description || `Test ${i + 1}`}
                    {test.input    && <span className="ml-2 font-mono">→ entrée: {test.input}</span>}
                    {test.expected && <span className="ml-2 font-mono">attendu: {test.expected}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (type === 'interactive') {
      return (
        <div className="space-y-4">
          {(content.slides || []).map((slide, i) => (
            <div key={slide.id || i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">{slide.title}</h4>
              {slide.content && <p className="text-gray-600 dark:text-gray-400 mb-3">{slide.content}</p>}
              {slide.code && (
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{slide.code}</code>
                </pre>
              )}
            </div>
          ))}
          {content.sandbox?.starterCode && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Éditeur de code</h3>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{content.sandbox.starterCode}</code>
              </pre>
            </div>
          )}
        </div>
      );
    }

    if (type === 'assignment') {
      return (
        <div className="space-y-4">
          {content.instructions && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Instructions</h3>
              <p className="text-gray-600 dark:text-gray-400">{content.instructions}</p>
            </div>
          )}
          {content.rubric?.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white mb-2">Critères d'évaluation</h3>
              <div className="space-y-2">
                {content.rubric.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                    <span className="text-gray-800 dark:text-gray-200">{r.criterion}</span>
                    <span className="font-medium text-primary">{r.points} pts</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {content.dueDate && (
            <p className="text-sm text-gray-500">
              Date limite : {new Date(content.dueDate).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      );
    }

    // Fallback text / type inconnu
    return (
      <div className="prose dark:prose-invert max-w-none">
        {content.text
          ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.text, SAFE_HTML_CONFIG) }} />
          : <p className="text-gray-500 italic">Aucun contenu disponible pour ce type de leçon.</p>
        }
      </div>
    );
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  function getEmbedUrl(url) {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    // URL directe (Cloudinary, MP4…) → rendu via <video> natif, pas iframe
    return null;
  }

  // ── Évaluation : bannière par module ─────────────────────────────────────
  const renderModuleEvaluationBanner = (module) => {
    const ev = evalByModule[module.id];
    if (!ev) {
      return (
        <div className="mx-3 mb-3 p-3 bg-gray-50 dark:bg-gray-900/30 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Pas d'évaluation pour ce module
        </div>
      );
    }
    const passed = ev.user_passed;
    // Pas d'évaluation sans avoir vu chaque vidéo du module (80 % de chacune) — règle aussi appliquée par le serveur
    const videosRestantes = (module.lessons || []).filter(l => l.type === 'video' && l.is_published !== false && !watchedVideoIds.has(l.id));
    const attempts = ev.user_attempts_count || 0;
    const maxA = ev.max_attempts;
    const remaining = maxA ? Math.max(0, maxA - attempts) : null;
    return (
      <div className={`mx-3 mb-3 p-3 rounded-lg border ${passed
        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        : 'bg-primary/5 dark:bg-primary/10 border-primary/30'}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Target className={`w-4 h-4 ${passed ? 'text-green-600' : 'text-primary'}`} />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                Évaluation : {ev.title}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Score requis : {ev.passing_score}%
                {ev.user_latest_attempt && (
                  <> · Dernier score : {Math.round(Number(ev.user_latest_attempt.percentage))}%</>
                )}
                {maxA !== null && maxA !== undefined && (
                  <> · Tentatives : {attempts}/{maxA}</>
                )}
              </div>
            </div>
          </div>
          {passed ? (
            <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-300 text-xs font-medium">
              <CheckCircle className="w-4 h-4" /> Validé
            </span>
          ) : (
            <button
              onClick={() => handleStartEvaluation(ev.id)}
              disabled={remaining === 0 || videosRestantes.length > 0}
              title={videosRestantes.length > 0 ? 'Regardez d\'abord les vidéos du module' : undefined}
              className="text-xs bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {attempts > 0 ? 'Refaire l\'évaluation' : 'Passer l\'évaluation'}
            </button>
          )}
        </div>
        {!passed && videosRestantes.length > 0 && (
          <div className="mt-2 text-xs text-gray-700 dark:text-gray-300">
            <p className="font-medium">
              Regardez d'abord {videosRestantes.length > 1 ? `ces ${videosRestantes.length} vidéos` : 'cette vidéo'} (au moins 80 % de chacune) :
            </p>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {videosRestantes.map(l => <li key={l.id}>{l.title}</li>)}
            </ul>
          </div>
        )}
        {ev.user_latest_attempt && !passed && (
          <div className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <XCircle className="w-3 h-3" /> Score insuffisant, réessayez
          </div>
        )}
      </div>
    );
  };

  // ── Bannière certificat ──────────────────────────────────────────────────
  const renderCertificateBanner = () => {
    if (!serverProgress) return null;

    // Certificat déjà obtenu
    if (serverProgress.certificate_issued && serverProgress.certificate) {
      return (
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Certificat obtenu !</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Score moyen : {Math.round(Number(serverProgress.certificate.score || 0))}%
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/certificates/${serverProgress.certificate.id}`)}
              className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium"
            >
              Voir le certificat
            </button>
          </div>
        </div>
      );
    }

    const totalEv = serverProgress.evaluations_total || 0;
    const passedEv = serverProgress.evaluations_passed || 0;
    const unwatchedVideos = serverProgress.unwatched_video_lessons || [];

    // Vidéos non visionnées — blocage certificat
    if (unwatchedVideos.length > 0) {
      return (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                Certificat bloqué — {unwatchedVideos.length} vidéo{unwatchedVideos.length > 1 ? 's' : ''} non visionnée{unwatchedVideos.length > 1 ? 's' : ''}
              </p>
              <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5 list-disc list-inside">
                {unwatchedVideos.map(v => <li key={v.id}>{v.title}</li>)}
              </ul>
            </div>
            <button onClick={refreshProgress} className="text-xs text-primary hover:text-primary/80 flex-shrink-0">
              Rafraîchir
            </button>
          </div>
        </div>
      );
    }

    if (totalEv === 0) return null;
    return (
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3">
          <Award className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Certificat : {passedEv}/{totalEv} évaluations validées
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {passedEv >= totalEv
                ? (reclamation.raison || 'Toutes les évaluations sont réussies : obtenez votre certificat.')
                : 'Validez toutes les évaluations pour obtenir votre certificat'}
            </p>
            {reclamation.lienAchat && (
              <a href={reclamation.lienAchat} className="text-xs font-medium text-primary underline">Régler le certificat</a>
            )}
          </div>
          {passedEv >= totalEv && (
            <button onClick={reclamerCertificat} disabled={reclamation.enCours}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50 flex-shrink-0">
              {reclamation.enCours ? 'Vérification…' : 'Obtenir mon certificat'}
            </button>
          )}
          <button onClick={refreshProgress} className="text-xs text-primary hover:text-primary/80">
            Rafraîchir
          </button>
        </div>
      </div>
    );
  };

  // ── Rendu principal ───────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {courseContent.title || 'Cours'}
            </h1>
            {courseContent.description && (
              <p className="text-gray-600 dark:text-gray-400">{courseContent.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <Share2 className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Progression */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Progression globale</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {serverProgress
                ? `${serverProgress.lessons_completed}/${serverProgress.lessons_total} leçons · ${serverProgress.evaluations_passed}/${serverProgress.evaluations_total} évaluations`
                : `${progress.completed}/${progress.total} leçons`}
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <div className="text-right text-sm text-gray-600 dark:text-gray-400 mt-1">
            {progress.percentage}% terminé
          </div>
        </div>
      </div>

      {/* Bannière certificat */}
      {renderCertificateBanner()}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contenu du cours</h2>
            </div>
            <div
              ref={sidebarContentRef}
              className="p-4 overflow-y-auto lg:max-h-[calc(100vh-26rem)]"
              style={sidebarHeight != null ? { height: sidebarHeight, maxHeight: 'none' } : undefined}
            >
              <div className="space-y-2">
                {courseContent.modules.map((module) => {
                  const ev = evalByModule[module.id];
                  const modulePassed = ev?.user_passed;
                  return (
                    <div key={module.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                      <button
                        onClick={() => toggleModule(module.id)}
                        className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 dark:text-white truncate">{module.title}</h3>
                            {modulePassed && (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {(module.lessons || []).length} leçon{(module.lessons || []).length !== 1 ? 's' : ''}
                            {ev && <> · éval {ev.passing_score}% requis</>}
                          </p>
                        </div>
                        {expandedModules[module.id]
                          ? <ChevronDown  className="w-4 h-4 text-gray-400" />
                          : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      </button>

                      {expandedModules[module.id] && (
                        <div className="border-t border-gray-200 dark:border-gray-700">
                          {(module.lessons || []).map((lesson) => (
                            <button
                              key={lesson.id}
                              onClick={() => handleLessonClick(lesson, module.id)}
                              className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                currentLesson?.id === lesson.id ? 'bg-primary/10 dark:bg-primary/20' : ''
                              }`}
                            >
                              <div className={getLessonTypeColor(lesson.type)}>
                                {getLessonIcon(lesson.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {lesson.title}
                                  </h4>
                                  {lesson.completed && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                  <Clock className="w-3 h-3" />
                                  {lesson.duration || `${lesson.estimatedDuration || 5} min`}
                                </div>
                              </div>
                            </button>
                          ))}
                          {renderModuleEvaluationBanner(module)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Poignée de redimensionnement */}
            <div
              className="hidden lg:flex items-center justify-center h-4 cursor-ns-resize select-none rounded-b-lg border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              onMouseDown={(e) => {
                isDragging.current = true;
                dragStartY.current = e.clientY;
                dragStartHeight.current = sidebarContentRef.current?.offsetHeight ?? 0;
                document.body.style.cursor = 'ns-resize';
                document.body.style.userSelect = 'none';
              }}
            >
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-500" />
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="lg:col-span-2">
          {currentLesson ? (
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className={getLessonTypeColor(currentLesson.type)}>
                        {getLessonIcon(currentLesson.type)}
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {currentLesson.title}
                      </h2>
                    </div>
                    {allLessons.length > 1 && (
                      <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {currentLessonIndex + 1} / {allLessons.length}
                      </span>
                    )}
                  </div>
                  {renderLessonContent(currentLesson)}
                </div>
              </div>

              {/* Navigation Précédent / Suivant */}
              {allLessons.length > 1 && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => goToLesson(currentLessonIndex - 1)}
                    disabled={currentLessonIndex <= 0}
                    className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Précédent
                  </button>
                  <div className="flex-1 text-center text-sm text-gray-500 dark:text-gray-400 truncate">
                    {currentLessonIndex > 0 && (
                      <span className="hidden sm:inline">← {allLessons[currentLessonIndex - 1]?.title}</span>
                    )}
                  </div>
                  {(() => {
                    const isLastLesson = currentLessonIndex >= allLessons.length - 1;
                    const isVideoUnwatched = currentLesson?.type === 'video' && !watchedVideoIds.has(currentLesson?.id);
                    const isBlocked = isLastLesson || isVideoUnwatched;
                    return (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          onClick={() => goToLesson(currentLessonIndex + 1)}
                          disabled={isBlocked}
                          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          Suivant
                          <ChevronRight className="w-4 h-4" />
                        </button>
                        {isVideoUnwatched && !isLastLesson && (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            Terminez la vidéo pour continuer
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <BookOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Chargement du cours…
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Veuillez patienter ou sélectionnez une leçon dans le menu de gauche.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseViewer;
